import React from 'react'
import { PanelRule, PanelSegmentButton, PanelSegmentSelect } from 'components/menu/panel'
import { ITableColumn } from 'types'
import { IFilterType, IUIFilter } from 'components/spreadsheet/types'
import { getDefaultFilterTypeForColType, isValidFilterTypeForColType } from 'components/spreadsheet/helpers/filtering'
import { FilterTypesToDescription } from 'components/spreadsheet/constants/const'
import FilterValue from './FilterValue'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'

interface Props {
  uiFilter: IUIFilter
  position: number
  columns: ITableColumn[]
  onChange: (value: IUIFilter) => void
  onDelete: (value: IUIFilter) => void
  onChangeGrouping: (uiFilter: IUIFilter, grouping: string) => void
  optionsOpen?: boolean
}

const FilterItem: React.FC<Props> = ({
  uiFilter,
  position,
  columns,
  onChange,
  onDelete,
  onChangeGrouping,
  optionsOpen
}) => {
  const column = columns.find((c) => c.publicId === uiFilter.filter.columnId)
  if (!column) return null
  const columnOptions = columns.map((column) => ({
    icon: <IconSelector kind={column.kind} />,
    label: column.name,
    value: column.publicId
  }))

  const handleColumnChange = (columnId: string) => {
    const newColumn = columns.find((c) => c.publicId === columnId)
    if (!newColumn) return
    const newFilter = {
      ...uiFilter,
      filter: {
        ...uiFilter.filter,
        columnId: columnId,
        columnName: newColumn.name,
        multipleValues: []
      }
    }

    // use the default filter type for the new column
    const filterType = getDefaultFilterTypeForColType(newColumn.kind)
    newFilter.filter.filterType = filterType

    onChange(newFilter)
  }

  // when the filter type is changed
  const handleFilterTypeChange = (filterType: IFilterType) => {
    const newFilter = {
      ...uiFilter,
      filter: {
        ...uiFilter.filter,
        filterType: filterType
      }
    }
    onChange(newFilter)
  }

  // when value is changed
  const handleChangeValue = (value: string) => {
    const newFilter = {
      ...uiFilter,
      filter: {
        ...uiFilter.filter,
        value: value
      }
    }
    onChange(newFilter)
  }

  // when multiple values are changed
  const handleChangeMulti = (values: string[]) => {
    const newFilter = {
      ...uiFilter,
      filter: {
        ...uiFilter.filter,
        multipleValues: values
      }
    }
    onChange(newFilter)
  }

  const handleGroupingChange = (value: string) => {
    onChangeGrouping(uiFilter, value)
  }

  // Types option list
  const typesOptions = Object.values(IFilterType)
    .reduce(
      (acc, type) => {
        acc.push({
          value: type,
          label: FilterTypesToDescription[type]
        })
        return acc
      },
      [] as { label: string; value: IFilterType }[]
    )
    .filter((option) => {
      return column && column.kind && isValidFilterTypeForColType(column.kind, option.value)
    })
  const isOr = uiFilter.filter.orGroup !== 'main' && uiFilter.filter.orGroup !== null

  return (
    <PanelRule
      id={uiFilter.cid}
      removeLabel={`Remove filter on ${column.name}`}
      onRemove={() => onDelete(uiFilter)}
      data-testid="filter-item"
    >
      {position > 0 && (
        <PanelSegmentButton
          tone="muted"
          label={isOr ? 'Switch to and' : 'Switch to or'}
          onClick={() => handleGroupingChange(isOr ? 'and' : 'or')}
        >
          {isOr ? 'or' : 'and'}
        </PanelSegmentButton>
      )}
      <PanelSegmentSelect
        ariaLabel="Column"
        tone="field"
        options={columnOptions}
        selected={[uiFilter.filter.columnId]}
        onPick={handleColumnChange}
        menuWidth={240}
        className="max-w-[180px]"
      />
      <PanelSegmentSelect
        ariaLabel="Condition"
        tone="muted"
        options={typesOptions}
        selected={uiFilter.filter.filterType ? [uiFilter.filter.filterType] : []}
        onPick={(option) => handleFilterTypeChange(option as IFilterType)}
        searchable={false}
        menuWidth={160}
      />
      <FilterValue
        column={column}
        filterType={uiFilter.filter.filterType}
        value={uiFilter.filter.value}
        multipleValues={
          uiFilter.filter.multipleValues
            ? uiFilter.filter.multipleValues.map((value) =>
                value !== undefined && value !== null ? value?.toString() : ''
              )
            : []
        }
        onChange={handleChangeValue}
        onChangeMultipleValues={handleChangeMulti}
        optionsOpen={optionsOpen}
      />
    </PanelRule>
  )
}

export default FilterItem
